import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import { FormDataType } from '@tn/formbuilder/utils/schema-types';
import { TrackingCategory } from '@tn/foundation/entities/tracking/const';
import { TrackerEngine } from '@tn/foundation/entities/tracking/lib/tracker-engine';
import type { SignUpResponse } from '@tn/foundation/graphql/__generated/gql/graphql';
import { SignUpFormFields } from '@tn/foundation/graphql/__generated/gql/graphql';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { ACTIVATE_LOCALSTORAGE_KEYS } from '@tn/foundation/utils/constants/signup';
import {
    APP_LOG_TYPE,
    PAGE_TYPES,
    TRACK_LINK_NAMES,
} from '@tn/foundation/utils/constants/tracking';
import request from 'graphql-request';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { cleanFormData } from '../../utils/cleanFormData/cleanFormData';
import { fetchCmpConsentTokenActor } from '../consent';
import { getEvaluatedRoute } from '../helpers';
import { shouldUseIdentityService } from '../utils/identityService';

import {
    identityRegisterMutation,
    identityRegisterRequestSource,
    signUpCacheKey,
    signUpMutation,
} from './graphql/constants';
import { SIGNUP_API_CLIENT_ERROR_CODES } from './graphql/constants/errorCodes';
import { runPostSignUpClientSetup } from './runPostSignUpClientSetup';

export type SignUpFlowContext = {
    fullName: string;
    email: string;
    password: string;
    zip: string;
    birthday: string;
    gender: string;
    optIn: boolean;
    userId: string;
    formBuilderRef: FormBuilderRef | null;
    formError: string | null;
    url: string;
    passIsVisible?: '0' | '1';
    skipOptInTermsPaModifiers?: boolean;
    redirectUrl?: string;
    activationCode?: string;
    isCodeFromQueryAllowed?: boolean;
};

function getUnsuccessfulSignUpErrorMessage(signUp: SignUpResponse | null | undefined): string {
    if (signUp?.message) {
        return signUp.message;
    }

    if (signUp?.errors) {
        return signUp.errors;
    }

    return SIGNUP_API_CLIENT_ERROR_CODES.DEFAULT;
}

type FieldError = { field: string; message: string };

function rejectSignUp(
    errorMessage: string,
    extensions?: { code?: string | null; fieldErrors?: FieldError[] | null },
): never {
    throw Object.assign(new Error(errorMessage), {
        response: { errors: [{ message: errorMessage, extensions }] },
    });
}

function rejectUnsuccessfulSignUp(signUp: SignUpResponse | null | undefined): never {
    rejectSignUp(getUnsuccessfulSignUpErrorMessage(signUp));
}

export const submitFormSignUpActor: FlowEntity = {
    initActor: ({ queryClient, getConfigByKey }) =>
        fromPromise<Pick<SignUpFlowContext, 'userId'>, Omit<SignUpFlowContext, 'userId'>>(
            async ({ input }) => {
                const cleanedFormData = cleanFormData(input as FormDataType);
                const graphqlEndpoint = `/api/tn/signup/graphql/`;

                if (shouldUseIdentityService(queryClient)) {
                    const res = await request(
                        graphqlEndpoint,
                        identityRegisterMutation,
                        { formFields: cleanedFormData as SignUpFormFields },
                        { 'request-source': identityRegisterRequestSource },
                    );

                    const { success, userId, message, errorCode, fieldErrors } =
                        res.identityRegister ?? {};
                    if (!success || !userId) {
                        // trackErrorEvent reads extensions.code.
                        rejectSignUp(message || SIGNUP_API_CLIENT_ERROR_CODES.DEFAULT, {
                            code: errorCode,
                            fieldErrors,
                        });
                    }

                    await runPostSignUpClientSetup({ queryClient, getConfigByKey });

                    return { userId };
                }

                const res = await queryClient.fetchQuery({
                    queryKey: [signUpCacheKey, cleanedFormData],
                    queryFn: () => {
                        const requestHeaders = {
                            'request-source': signUpCacheKey,
                        };

                        return request(
                            graphqlEndpoint,
                            signUpMutation,
                            { formFields: cleanedFormData as SignUpFormFields },
                            {
                                ...requestHeaders,
                            },
                        );
                    },
                });

                if (res.signUp?.success) {
                    await runPostSignUpClientSetup({ queryClient, getConfigByKey });

                    return res.signUp as Pick<SignUpFlowContext, 'userId'>;
                }

                rejectUnsuccessfulSignUp(res.signUp);
            },
        ),

    inputActor: <
        Mapper<
            SignUpFlowContext,
            AnyEventObject,
            Omit<
                SignUpFlowContext,
                | 'userId'
                | 'formBuilderRef'
                | 'url'
                | 'passIsVisible'
                | 'skipOptInTermsPaModifiers'
                | 'formError'
                | 'isCodeFromQueryAllowed'
                | 'activationCode'
            >,
            AnyEventObject
        >
    >(({
        context: {
            userId: _userId,
            formBuilderRef: _formBuilderRef,
            url: _url,
            passIsVisible: _passIsVisible,
            skipOptInTermsPaModifiers: _skipOptInTermsPaModifiers,
            formError: _formError,
            isCodeFromQueryAllowed: _isCodeFromQueryAllowed,
            activationCode: _activationCode,
            ...rest
        },
    }) => {
        return { ...rest };
    }),
};

export const signUpFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router } = rawParams as InitMachineParams;
        const machine = setup({
            types: {
                context: {} as SignUpFlowContext,
                events: {} as
                    | {
                          type: 'SUBMIT_CLICK';
                          params: Omit<SignUpFlowContext, 'userId'>;
                      }
                    | {
                          type: 'CONNECT_FORMBUILDER_REF';
                          params: FormBuilderRef;
                      }
                    | { type: 'SUBMITTING' }
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitFormSignUpActor.initActor>>,
                          'submitting'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitFormSignUpActor.initActor>>,
                          'submitting'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof fetchCmpConsentTokenActor.initActor>>,
                          'fetchCmpConsentToken'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof fetchCmpConsentTokenActor.initActor>>,
                          'fetchCmpConsentToken'
                      >,
                input: {} as { url: string },
            },
            guards: {},
            actions: {
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.assign({ ...event.params, formError: null });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                saveDataFromSuccessfulSignUp: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    enqueue.assign({ userId: event.output.userId });
                    context.formBuilderRef?.toggleLoading(false);

                    const ottActivationPartner =
                        typeof window !== 'undefined'
                            ? localStorage.getItem(
                                  ACTIVATE_LOCALSTORAGE_KEYS.LOCAL_ORIGINATING_CLIENTID_KEY,
                              )
                            : null;
                    const activationDeviceFamily =
                        typeof window !== 'undefined'
                            ? localStorage.getItem(
                                  ACTIVATE_LOCALSTORAGE_KEYS.LOCAL_PLATFORMCODE_KEY,
                              )
                            : null;
                    const activationShortCode = context.activationCode ?? null;

                    void TrackerEngine.update({
                        type: TRACK_LINK_NAMES.TRACK_REGISTRATION_NEW,
                        category: TrackingCategory.App.INTERACT,
                        detail: {
                            context: {
                                pageType: PAGE_TYPES.SIGN_UP,
                                ...(ottActivationPartner ? { ottActivationPartner } : {}),
                                ...(activationDeviceFamily ? { activationDeviceFamily } : {}),
                                ...(activationShortCode ? { activationShortCode } : {}),
                            },
                        },
                    });
                }),
                goToSignUpRoute: ({ context }) => {
                    const evaluatedUrl = getEvaluatedRoute(router.pathname, context.url);
                    if (evaluatedUrl) {
                        router.push(context.url);
                    }
                },
                assignFormBuilderRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });
                }),
                setError: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage =
                        event.error.response?.errors?.[0]?.message || event.error.message;
                    context.formBuilderRef?.toggleLoading(false);
                    enqueue.assign({ formError: errorMessage });
                }),
                handleError: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleError(event.error.message);
                },
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage =
                        event.error.response?.errors?.[0]?.message || event.error.message;
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: errorMessage,
                        variant: 'critical',
                    });
                    const fieldErrors: FieldError[] | undefined =
                        event.error.response?.errors?.[0]?.extensions?.fieldErrors;
                    fieldErrors?.forEach(({ field, message }) =>
                        context.formBuilderRef?.toggleFormError(field, message),
                    );
                },
                trackErrorEvent: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage =
                        event.error.response?.errors?.[0]?.message || event.error.message;
                    const errorCode =
                        event.error.response?.errors?.[0]?.extensions?.code || 'No code provided';

                    // Failure-boundary telemetry for the sign-up submit actor. Log the stable
                    // error code only — never the raw API message (may contain PII).
                    appLogger.error('Sign-up submission failed', {
                        component: 'signUpFlow',
                        errorCode,
                    });

                    void TrackerEngine.update({
                        type: TRACK_LINK_NAMES.TRACK_APP_LOG,
                        category: TrackingCategory.App.LOG,
                        detail: {
                            context: {
                                pageType: PAGE_TYPES.SIGN_UP,
                                appLogType: APP_LOG_TYPE.SIGN_UP_ERROR,
                                appLogCode: errorCode,
                                appLogText: errorMessage,
                                passIsVisible: context.passIsVisible ?? '0',
                            },
                        },
                    });
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                fullName: '',
                email: '',
                password: '',
                zip: '',
                birthday: '',
                gender: '',
                userId: '',
                optIn: false,
                formBuilderRef: null,
                formError: null,
                passIsVisible: '0',
                ...input,
            }),
        });

        return machine;
    },

    inputActor: <
        Mapper<
            SignUpFlowContext,
            AnyEventObject,
            Omit<
                SignUpFlowContext,
                'userId' | 'formBuilderRef' | 'url' | 'passIsVisible' | 'redirectUrl' | 'formError'
            >,
            AnyEventObject
        >
    >(({
        context: {
            userId: _userId,
            formBuilderRef: _formBuilderRef,
            url: _url,
            passIsVisible: _passIsVisible,
            redirectUrl: _redirectUrl,
            formError: _formError,
            ...rest
        },
    }) => {
        return { ...rest };
    }),

    outputActor: <
        Mapper<
            SignUpFlowContext,
            AnyEventObject,
            { userId: string; formBuilderRef: FormBuilderRef | null },
            AnyEventObject
        >
    >(({ context }) => {
        return { ...context };
    }),
};
